import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import Config from 'config';
import AppHeaderOptionsMenu from 'components/@layout/AppBar/AppHeaderOptionsMenu';
import ARCHIVE_CLASSROOM_MUTATION from 'graph/classroom/archive-classroom-mutation';
import DELETE_CLASSROOM_MUTATION from 'graph/classroom/remove-classroom-mutation';
import REMOVE_STUDENTS from 'graph/student/remove-students-mutation';
import useRouteAction from 'hooks/route-action/use-route-action';
import { ModificationAlertContent } from 'hooks/sweet-alert/use-modification-alert';
import { makeStyles } from '@material-ui/core/styles';
import { ModalVariant } from 'providers/ModalProvider';
import useStore from 'store';
import { SweetAlertOptions } from 'sweetalert2';
import { getActiveStudentProducts } from 'utilities/product-utils';
import { StudentRowData } from '../../Classrooms/modals/RemoveStudentModal/components/StudentSelectTable';
import RemoveStudentModal from '../../Classrooms/modals/RemoveStudentModal';
import AddStudentModal from '../../Classrooms/modals/AddStudentsModal';
import ClassroomSettingsModal from '../../Classrooms/modals/ClassroomSettingsModal';
import EditClassroomModal from '../../Classrooms/modals/EditClassroomModal';
import ManageClassroomProductsModal from '../../Classrooms/modals/ManageClassroomProductsModal';
import PrintStudentLoginCardsModal from '../../Classrooms/modals/PrintStudentLoginCardsModal';
import PrintCertificatesCardsModal from '../../Classrooms/modals/PrintCertificatesCardsModal';
import {
  getClassroomMutationCacheUpdate,
  getClassroomsMutationCacheUpdate
} from '../../util/update-cache';
import { ModuleProps, VoidCallback } from 'types';
import { Classroom } from 'types/schema-generated-types';
import { AlertVariant } from 'types/enums';
import { ClassroomModalVariant, ClassroomModalRouteAction } from '../../types';

const { privilegesGroups } = Config;

export interface MoreOptionsHandlers {
  openClassroomModal: (modalVariant: ClassroomModalVariant) => VoidCallback;
  onArchiveClick: VoidCallback;
  onDeleteClick: VoidCallback;
  onDeleteStudentsClick: VoidCallback;
  classroomId: number;
}

interface ModalHandlers {
  closeModal: VoidCallback;
}

interface ClassroomModalDetails {
  routeAction: ClassroomModalRouteAction;
  title: string | null;
  component: JSX.Element;
  modalVariant: ModalVariant;
}

const useStyles = makeStyles(() => ({
  moreOptionsIcon: {
    color: 'black'
  }
}));

const modalFactory = (
  modalVariant: ClassroomModalVariant,
  { closeModal: handleOnClose }: ModalHandlers
): ClassroomModalDetails =>
  ({
    [ClassroomModalVariant.EDIT]: {
      routeAction: ClassroomModalRouteAction.EDIT_CLASSROOM,
      title: 'Edit Class',
      component: <EditClassroomModal onClose={handleOnClose} />,
      modalVariant: ModalVariant.QUARTER
    },
    [ClassroomModalVariant.ADD_STUDENT]: {
      routeAction: ClassroomModalRouteAction.ADD_STUDENTS,
      title: null,
      component: <AddStudentModal onClose={handleOnClose} />,
      modalVariant: ModalVariant.FULL
    },
    [ClassroomModalVariant.MANAGE_CLASSROOM_SETTNGS]: {
      routeAction: ClassroomModalRouteAction.MANAGE_CLASSROOM_SETTINGS,
      title: null,
      component: <ClassroomSettingsModal />,
      modalVariant: ModalVariant.FULL
    },
    // TODO
    [ClassroomModalVariant.MANAGE_CLASSROOM_PRODUCTS]: {
      routeAction: ClassroomModalRouteAction.MANAGE_CLASSROOM_PRODUCTS,
      title: null,
      component: <ManageClassroomProductsModal onClose={handleOnClose} />,
      modalVariant: ModalVariant.FULL
    },
    [ClassroomModalVariant.CREATE]: {
      routeAction: ClassroomModalRouteAction.CREATE_CLASSROOM,
      title: null,
      component: <div />,
      modalVariant: ModalVariant.FULL
    },
    [ClassroomModalVariant.ARCHIVE]: {
      routeAction: ClassroomModalRouteAction.ARCHIVE_CLASSROOMS,
      title: null,
      component: <div />,
      modalVariant: ModalVariant.FULL
    },
    [ClassroomModalVariant.DELETE]: {
      routeAction: ClassroomModalRouteAction.DELETE_CLASSROOMS,
      title: null,
      component: <div />,
      modalVariant: ModalVariant.FULL
    }
  }[modalVariant]);

const ClassroomOptionsMenu: React.FC<ModuleProps & {
  setLoading: (value: boolean) => void;
  classroom: Classroom;
}> = ({ id, classroom, setLoading }) => {
  const { t } = useTranslation(id);
  const history = useHistory();

  const classes = useStyles();
  const [
    { userPrivilegesCache, user },
    { openFullModal, openHalfModal, openQuarterModal, closeModal, openAlert }
  ] = useStore();
  const { params: { classroomId = '' } = {} } = useRouteMatch<{ classroomId: string }>();

  const [removeStudents] = useMutation(
    REMOVE_STUDENTS,
    getClassroomMutationCacheUpdate(Number(classroomId))
  );
  const [archiveClassroom] = useMutation(
    ARCHIVE_CLASSROOM_MUTATION,
    getClassroomsMutationCacheUpdate(Number(user?.me?.id))
  );
  const [deleteClassroom] = useMutation(
    DELETE_CLASSROOM_MUTATION,
    getClassroomsMutationCacheUpdate(Number(user?.me?.id))
  );

  const archived = Boolean(classroom.dateArchived);

  //TODO: Add error handling
  const showArchiveAlertCallback = React.useCallback(
    (title: string, text: string) => async (): Promise<void> => {
      setLoading(true);
      await archiveClassroom({ variables: { classroomId: classroom.id } });
      openAlert({
        variant: AlertVariant.SUCCESS,
        title,
        text
      });
      setLoading(false);
    },
    [openAlert, archiveClassroom, setLoading, classroom.id]
  );

  //TODO: Add error handling
  const showDeleteAlertCallback = React.useCallback(
    (title: string, text: string) => async (): Promise<void> => {
      setLoading(true);
      await deleteClassroom({ variables: { classroomId: classroom.id } });
      openAlert({
        variant: AlertVariant.SUCCESS,
        title,
        text
      });
      setLoading(false);
      history.push('/classrooms');
    },
    [openAlert, deleteClassroom, setLoading, classroom.id, history]
  );

  const showArchiveAlert = React.useCallback((): void => {
    openAlert({
      variant: AlertVariant.CLASSROOM_ARCHIVE,
      textItems: [classroom.name ?? ''],
      onSubmit: showArchiveAlertCallback(
        t('successTitle', { ns: 'ArchiveClassroomsAlert' }),
        t('successMessage', { ns: 'ArchiveClassroomsAlert' })
      )
    });
  }, [openAlert, classroom, t, showArchiveAlertCallback]);

  const showDeleteAlert = React.useCallback(async (): Promise<void> => {
    const textItems = [classroom.name ?? ''];
    const deleteText = t('deleteConfirmInstructions', { ns: '@common' });
    const title = t('title', { count: textItems.length, ns: 'DeleteClassroomsAlert' });
    const confirmButtonText = t('confirm', {
      count: textItems.length,
      ns: 'DeleteClassroomsAlert'
    });

    openAlert({
      variant: AlertVariant.DELETE,
      title,
      confirmButtonText,
      deleteText,
      html: <ModificationAlertContent textItems={textItems} deleteText={deleteText} />,
      onSubmit: showDeleteAlertCallback(
        t('successTitle', { ns: 'DeleteClassroomsAlert' }),
        t('successMessage', { ns: 'DeleteClassroomsAlert' })
      )
    });
  }, [openAlert, classroom, t, showDeleteAlertCallback]);

  const handleCloseProductModal = (success?: boolean): void => {
    closeModal();
  };

  const openClassroomProductModal = (): void => {
    openFullModal({
      body: <ManageClassroomProductsModal onClose={handleCloseProductModal} />,
      options: { hideCloseButton: true },
      routeAction: ClassroomModalRouteAction.MANAGE_CLASSROOM_PRODUCTS,
      routeParams: { classroomId }
    });
  };

  const openClassroomModal = (classroomModalVariant: ClassroomModalVariant) => (): void => {
    const modal = modalFactory(classroomModalVariant, { closeModal });

    let openModal = openFullModal;
    if (modal.modalVariant === ModalVariant.QUARTER) openModal = openQuarterModal;
    if (modal.modalVariant === ModalVariant.HALF) openModal = openHalfModal;

    openModal({
      title: modal.title,
      body: modal.component,
      routeAction: modal.routeAction,
      routeParams: { classroomId: classroom.id }
    });
  };

  const handleConfirmedDelete = (studentIds: Array<number>) => async (): Promise<void> => {
    try {
      await removeStudents({ variables: { where: { id: studentIds } } });

      openAlert({
        variant: AlertVariant.SUCCESS,
        title: t('successTitle', { count: studentIds.length, ns: 'DeleteStudentsAlert' }),
        text: t('successMessage', { count: studentIds.length, ns: 'DeleteStudentsAlert' })
      });
    } catch (error) {
      //TODO: Handle error for delete
      console.log('Error', error);
    }
  };

  const handleConfirmDeleteStudent = (
    { overrides }: { overrides?: SweetAlertOptions },
    selectedStudents: StudentRowData[]
  ): void => {
    const selectedIds = selectedStudents.map(({ id }: StudentRowData): number => Number(id));
    const deleteText = t('deleteConfirmInstructions', { ns: '@common' });
    const title = t('title', { count: selectedIds.length, ns: 'DeleteStudentsAlert' });
    const confirmButtonText = t('confirm', {
      count: selectedIds.length,
      ns: 'DeleteStudentsAlert'
    });

    openAlert({
      variant: AlertVariant.DELETE,
      title,
      confirmButtonText,
      deleteText,
      html: overrides?.html,
      onSubmit: handleConfirmedDelete(selectedIds)
    });
  };

  const openDeleteStudentsModal = (): void => {
    const students =
      classroom.students?.map(
        ({
          id,
          firstName,
          lastName,
          username,
          email,
          gradeLevel: grade,
          licenseUsage,
          groups
        }) => ({
          id,
          firstName,
          lastName,
          username,
          email,
          gradeLevel: grade,
          group: groups?.find(group => group.classroomId === classroom.id)?.name ?? 'NA',
          products: licenseUsage ? getActiveStudentProducts(licenseUsage) : []
        })
      ) || ([] as Array<StudentRowData>);
    openFullModal({
      body: (
        <RemoveStudentModal
          rowData={students}
          onClose={closeModal}
          onConfirm={handleConfirmDeleteStudent}
        />
      )
    });
  };

  const openPrintLoginCardsModal = (): void => {
    openQuarterModal({
      body: <PrintStudentLoginCardsModal />,
      routeAction: ClassroomModalRouteAction.PRINT_STUDENT_LOGIN_CARDS,
      routeParams: { classroomId }
    });
  };

  const openPrintCertificatesModal = (): void => {
    openQuarterModal({
      body: <PrintCertificatesCardsModal />,
      routeAction: ClassroomModalRouteAction.PRINT_STUDENT_CERTIFICATES,
      routeParams: { classroomId }
    });
  };

  const archivedClassroomOptions = [
    {
      label: 'Delete Class',
      onClick: showDeleteAlert,
      privileges: privilegesGroups.mutateClassroom
    }
  ];
  const archivedStudentOptions = [
    {
      label: 'Delete Students',
      onClick: openDeleteStudentsModal,
      privileges: privilegesGroups.mutateStudent
    }
  ];

  const classOptions = !archived
    ? [
        {
          label: 'class-actions',
          title: 'Class Actions',
          options: [
            {
              label: 'Manage Class Settings',
              onClick: openClassroomModal(ClassroomModalVariant.MANAGE_CLASSROOM_SETTNGS)
            },
            /*  {
              label: 'Export Data to CSV'
            },
            */
            {
              label: 'Edit Class Details',
              onClick: openClassroomModal(ClassroomModalVariant.EDIT),
              privileges: privilegesGroups.mutateClassroom
            },
            {
              label: 'Archive Class',
              onClick: showArchiveAlert,
              privileges: privilegesGroups.mutateClassroom
            },
            ...archivedClassroomOptions
          ]
        }
      ]
    : [
        {
          label: 'class-actions',
          title: 'Class Actions',
          options: [...archivedClassroomOptions]
        }
      ];

  const studentOptions = !archived
    ? [
        {
          label: 'student-actions',
          title: 'Student Actions',
          options: [
            {
              label: 'Manage Student Products',
              onClick: openClassroomProductModal,
              privileges: [
                ...privilegesGroups.updateClassroomLicense,
                ...privilegesGroups.updateOrgLicense
              ]
            },
            {
              label: 'Print Student Login Cards',
              onClick: openPrintLoginCardsModal
            },
            {
              label: 'Print Student Certificates',
              onClick: openPrintCertificatesModal
            },
            /* {
              label: 'Transfer Students',
              privileges: privilegesGroups.mutateStudent
            },
            */
            ...archivedStudentOptions
          ]
        }
      ]
    : [
        {
          label: 'student-actions',
          title: 'Student Actions',
          options: [...archivedStudentOptions]
        }
      ];

  const options = [...classOptions, ...studentOptions].map(x => {
    return {
      ...x,
      options: (x.options as any).filter((y: any) =>
        userPrivilegesCache.isPrivileged(y.privileges ?? [], {
          classroomId: classroom.id,
          organizationId: classroom.organizationId
        })
      )
    };
  });

  useRouteAction(({ action }) => {
    if (!action) return;
    switch (action) {
      case ClassroomModalRouteAction.EDIT_CLASSROOM:
        return openClassroomModal(ClassroomModalVariant.EDIT)();
      case ClassroomModalRouteAction.ADD_STUDENTS:
        return openClassroomModal(ClassroomModalVariant.ADD_STUDENT)();
      case ClassroomModalRouteAction.MANAGE_CLASSROOM_SETTINGS:
        return openClassroomModal(ClassroomModalVariant.MANAGE_CLASSROOM_SETTNGS)();
      default:
        return null;
    }
  });

  return (
    <React.Fragment>
      <AppHeaderOptionsMenu
        buttonSize="small"
        id={'more-options-menu'}
        aria-label="More options menu."
        iconClassName={classes.moreOptionsIcon}
        optionSections={options}
      />
    </React.Fragment>
  );
};

export default ClassroomOptionsMenu;
